import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { DashboardTile } from './DashboardTile';
import { useAdminHealth, type AdminHealthDto, type AdminHealthServiceStatus } from '../../../api';
import { tokens } from '../../../design';
import { formatUptime } from '../../../utils/format';

/**
 * System-health tile — mirrors the `.t-health` tile in
 * `design/mockups/admin/dashboard.html`, but renders ONLY what
 * `GET /health` actually returns: overall status, the three downstream
 * service probes (db / redis / s3) and process uptime.
 *
 * Deliberately NOT rendered: the mockup's BullMQ / WebPay / Sumsub pills,
 * the uptime-% gauge, and the per-service latency figures — none of those
 * are in the payload, and faking them is the AUT-37 hardcoded-value trap.
 */

const StatusBadge = styled('span')<{ ok: boolean }>(({ ok }) => ({
  fontSize: 11,
  fontWeight: 700,
  padding: '2px 8px',
  borderRadius: 999,
  color: ok ? tokens.state.success : tokens.state.danger,
  background: ok ? tokens.state.successBg : tokens.state.dangerBg,
}));

const Pill = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 11,
});

const PillDot = styled('span')<{ ok: boolean }>(({ ok }) => ({
  width: 8,
  height: 8,
  borderRadius: 999,
  flexShrink: 0,
  background: ok ? tokens.state.success : tokens.state.danger,
  boxShadow: ok ? '0 0 0 3px rgba(22, 163, 106, 0.18)' : '0 0 0 3px rgba(214, 51, 75, 0.18)',
}));

type ServiceKey = 'db' | 'redis' | 's3';

interface ServiceRow {
  key: ServiceKey;
  status: AdminHealthServiceStatus;
}

const HealthServicePill = ({ svc }: { svc: ServiceRow }): ReactElement => {
  const { t } = useTranslation();
  const ok = svc.status === 'ok';
  return (
    <Pill>
      <PillDot ok={ok} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 12, color: tokens.text.primary }}>
          {t(`dashboard.health.services.${svc.key}`)}
        </Typography>
        <Typography sx={{ fontSize: 10, color: tokens.text.tertiary, fontFamily: tokens.font.mono }}>
          {ok ? t('dashboard.health.up') : t('dashboard.health.down')}
        </Typography>
      </Box>
    </Pill>
  );
};

const HealthBody = ({ data }: { data: AdminHealthDto }): ReactElement => {
  const { t } = useTranslation();
  const services: ServiceRow[] = [
    { key: 'db', status: data.services.db },
    { key: 'redis', status: data.services.redis },
    { key: 's3', status: data.services.s3 },
  ];
  return (
    <>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px 16px', mt: 1 }}>
        {services.map((svc) => (
          <HealthServicePill key={svc.key} svc={svc} />
        ))}
      </Box>
      <Typography sx={{ fontSize: 11, color: tokens.text.tertiary, mt: 'auto', pt: 2 }}>
        {t('dashboard.health.uptime')}:{' '}
        <Box component="span" sx={{ fontFamily: tokens.font.mono, color: tokens.text.secondary }}>
          {formatUptime(data.uptime)}
        </Box>
      </Typography>
    </>
  );
};

export const SystemHealthTile = ({ gridColumn }: { gridColumn?: string }): ReactElement => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useAdminHealth();
  const overallOk = data?.status === 'ok';

  return (
    <DashboardTile sx={{ gridColumn: gridColumn ?? 'span 6' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            color: tokens.text.tertiary,
          }}
        >
          {t('dashboard.health.title')}
        </Typography>
        {data ? (
          <StatusBadge ok={overallOk}>
            {overallOk ? t('dashboard.health.statusOk') : t('dashboard.health.statusDegraded')}
          </StatusBadge>
        ) : null}
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : null}

      {isError || (!isLoading && !data) ? (
        <Typography variant="body2" sx={{ color: tokens.state.danger, py: 2 }}>
          {t('dashboard.health.loadError')}
        </Typography>
      ) : null}

      {data ? <HealthBody data={data} /> : null}
    </DashboardTile>
  );
};
